import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { X, Download, Link as LinkIcon, Check } from "lucide-react";
import { track } from "@/lib/track";

/**
 * ShareCardModal — opens a quiet dialog showing the generated memorial
 * card image + share buttons for every relevant channel in Italy.
 *
 * Sharing strategy:
 *  - Mobile (Web Share API available): a single "Condividi" button
 *    opens the native share sheet — gives access to ALL installed
 *    apps (Instagram, TikTok, WhatsApp, FB, mail, AirDrop, etc.).
 *    This is the cleanest UX on phones.
 *  - Desktop: explicit per-platform buttons. Some platforms (IG, TikTok)
 *    don't accept image-via-URL sharing → for those we present the
 *    "Scarica" button so the user can post the saved file manually.
 *  - "Copia link" + "Scarica" are always present as universal fallbacks.
 *
 * The card itself is generated server-side (1080×1080 PNG, brand-themed).
 * The "share URL" pasted on social is a separate landing page with rich
 * OpenGraph tags so unfurling looks right on WhatsApp/FB/Telegram.
 */
export default function ShareCardModal({ presenceId, presenceName, onClose }) {
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [shareUrl, setShareUrl] = useState("");
    const [copied, setCopied] = useState(false);
    const [stats, setStats] = useState(null);
    const dialogRef = useRef(null);
    const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;

    // Generate (or regenerate) the card on mount. Owner-only endpoint:
    // server-side checks the JWT belongs to the presence's owner.
    useEffect(() => {
        let cancelled = false;
        track("share_modal_opened", { presence_id: presenceId });
        (async () => {
            try {
                const [{ data: card }, statsResp] = await Promise.all([
                    api.post(`/presences/${presenceId}/share-card`, {}),
                    // Stats endpoint is best-effort — failures shouldn't block the modal.
                    api.get(`/presences/${presenceId}/share-stats`).catch(() => ({ data: null })),
                ]);
                if (cancelled) return;
                setImageUrl(card.image_url);
                setShareUrl(card.share_url);
                setStats(statsResp?.data || null);
            } catch (e) {
                if (!cancelled) setErr(e?.response?.data?.detail || "Non riesco a creare il biglietto. Riprova fra un momento.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [presenceId]);

    // Lock body scroll while modal is open + ESC to close.
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener("keydown", onKey);
        };
    }, [onClose]);

    const shareText = `In ricordo di ${presenceName || "una presenza"}.`;

    const handleNativeShare = async () => {
        if (!navigator.share) return;
        try {
            await navigator.share({
                title: shareText,
                text: shareText,
                url: shareUrl,
            });
        } catch {
            // User cancelled the native sheet — silent.
        }
    };

    const handleDownload = async () => {
        // Fetch the image (we're cross-origin to backend, but the response
        // has Cache-Control public — works in all browsers).
        try {
            const r = await fetch(imageUrl, { credentials: "omit" });
            const blob = await r.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `ricordo-${(presenceName || "presenza").toLowerCase().replace(/\s+/g, "-")}.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(a.href), 1000);
        } catch {
            // Fallback: open in a new tab so user can save manually.
            window.open(imageUrl, "_blank", "noopener,noreferrer");
        }
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Some old browsers — fallback to prompt-style selection.
            const ta = document.createElement("textarea");
            ta.value = shareUrl;
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand("copy"); } catch { /* noop */ }
            ta.remove();
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    // Per-platform share URLs. We use the LANDING url (not the image
    // URL) so the recipient sees the full memorial page with copy.
    const enc = encodeURIComponent;
    const whatsappHref = `https://wa.me/?text=${enc(shareText + " " + shareUrl)}`;
    const facebookHref = `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`;
    const telegramHref = `https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(shareText)}`;
    const emailHref = `mailto:?subject=${enc(shareText)}&body=${enc(shareText + "\n\n" + shareUrl)}`;

    return (
        <div
            className="fixed inset-0 z-[200] flex items-center justify-center px-4 py-8 bg-ink-deep/80 backdrop-blur-sm animate-fade-in"
            onClick={(e) => { if (e.target === dialogRef.current) onClose(); }}
            ref={dialogRef}
            data-testid="share-card-modal"
        >
            <div className="relative w-full max-w-md bg-ink-paper rounded-sm border border-ink-textLow/20 shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 pt-6 pb-2">
                    <p className="label-eyebrow text-ink-gold">Condividi un ricordo</p>
                    <button
                        onClick={onClose}
                        className="text-ink-textLow hover:text-ink-textHigh transition-colors"
                        data-testid="share-card-close"
                        aria-label="Chiudi"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Card preview */}
                <div className="px-6 pb-4">
                    <p className="text-sm text-ink-textLow font-serif italic leading-relaxed mb-5">
                        Un'immagine sobria, da custodire o da condividere coi tuoi cari.
                    </p>
                    {loading ? (
                        <div
                            className="aspect-square bg-ink-deep/30 rounded-sm flex items-center justify-center text-sm text-ink-textLow font-serif italic animate-pulse"
                            data-testid="share-card-loading"
                        >
                            Sto preparando il biglietto…
                        </div>
                    ) : err ? (
                        <div
                            className="aspect-square bg-ink-deep/20 rounded-sm flex items-center justify-center text-sm text-ink-danger font-serif italic px-6 text-center"
                            data-testid="share-card-error"
                        >
                            {err}
                        </div>
                    ) : (
                        <>
                            <img
                                src={imageUrl}
                                alt={`In ricordo di ${presenceName || "una presenza"}`}
                                className="w-full aspect-square object-cover rounded-sm border border-ink-gold/20"
                                data-testid="share-card-preview"
                            />
                            {stats && stats.total_views > 0 && (
                                <ShareStats stats={stats} presenceName={presenceName} />
                            )}
                        </>
                    )}
                </div>

                {/* Actions */}
                {!err && !loading && (
                    <div className="px-6 pb-6">
                        {canNativeShare ? (
                            <button
                                onClick={handleNativeShare}
                                className="btn-primary w-full mb-2"
                                data-testid="share-card-native"
                            >
                                Condividi
                            </button>
                        ) : null}

                        {/* Per-platform row — always shown so desktop users can
                            pick directly without hunting through native sheets. */}
                        <div className="grid grid-cols-4 gap-2 mb-3">
                            <SharePill
                                href={whatsappHref}
                                label="WhatsApp"
                                color="#25D366"
                                testId="share-whatsapp"
                                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>}
                            />
                            <SharePill
                                href={facebookHref}
                                label="Facebook"
                                color="#1877F2"
                                testId="share-facebook"
                                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>}
                            />
                            <SharePill
                                href={telegramHref}
                                label="Telegram"
                                color="#229ED9"
                                testId="share-telegram"
                                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.464.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>}
                            />
                            <SharePill
                                href={emailHref}
                                label="Email"
                                color="#5a4a3a"
                                testId="share-email"
                                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>}
                            />
                        </div>

                        {/* Universal fallbacks — always work regardless of browser */}
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                onClick={handleDownload}
                                className="btn-secondary inline-flex items-center justify-center gap-2 text-sm"
                                data-testid="share-download"
                            >
                                <Download size={15} /> Scarica
                            </button>
                            <button
                                onClick={handleCopy}
                                className="btn-secondary inline-flex items-center justify-center gap-2 text-sm"
                                data-testid="share-copy"
                            >
                                {copied ? <><Check size={15} /> Copiato</> : <><LinkIcon size={15} /> Copia link</>}
                            </button>
                        </div>

                        {/* Quiet note for IG/TikTok which don't accept URL share */}
                        <p className="mt-4 text-xs text-ink-textLow font-serif italic leading-relaxed text-center">
                            Per Instagram o TikTok, scarica l'immagine e pubblicala dalla loro app.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

// Single-platform pill — renders an <a> link that opens the platform's
// share dialog in a new tab. Color tinted to the platform brand.
function SharePill({ href, label, color, icon, testId }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("share_platform", { platform: label.toLowerCase() })}
            className="flex flex-col items-center gap-1 py-3 rounded-sm border border-ink-textLow/20 hover:border-ink-textLow/40 transition-colors group"
            style={{ "--share-color": color }}
            data-testid={testId}
        >
            <span className="transition-colors text-ink-textMid group-hover:text-[var(--share-color)]">
                {icon}
            </span>
            <span className="text-[10px] tracking-wide uppercase text-ink-textLow group-hover:text-ink-textMid">
                {label}
            </span>
        </a>
    );
}

// ShareStats — quiet visualisation of how many people have seen this
// memorial. Deliberately small/calm: no big numbers, no leaderboards.
// Just a soft sentence + 7-day micro-bars sparkline.
function ShareStats({ stats, presenceName }) {
    const total = stats.total_views || 0;
    const last7 = stats.views_last_7d || 0;
    const max = Math.max(...(stats.daily || []).map((d) => d.views), 1);
    const totalLabel =
        total === 1
            ? "1 persona ha visto questo ricordo."
            : `${total} persone hanno visto questo ricordo.`;
    const trailingLabel =
        last7 > 0
            ? `${last7} negli ultimi 7 giorni.`
            : "Nessuna visita negli ultimi 7 giorni.";
    return (
        <div
            className="mt-5 px-4 py-4 border border-ink-gold/15 rounded-sm bg-ink-gold/5"
            data-testid="share-stats"
        >
            <p className="text-xs text-ink-textMid font-serif italic leading-relaxed text-center">
                {totalLabel}
            </p>
            <p className="text-[11px] text-ink-textLow font-serif italic text-center mt-1">
                {trailingLabel}
            </p>
            {/* Sparkline — 7 thin bars, oldest left → today right */}
            <div className="mt-4 flex items-end justify-center gap-1.5 h-6">
                {(stats.daily || []).map((d) => {
                    const h = Math.max(2, Math.round((d.views / max) * 22));
                    return (
                        <div
                            key={d.date}
                            className="w-1.5 bg-ink-gold/70 rounded-sm transition-all"
                            style={{ height: `${h}px` }}
                            title={`${d.date}: ${d.views}`}
                        />
                    );
                })}
            </div>
            <p className="text-[10px] text-ink-textLow tracking-widest uppercase text-center mt-2 opacity-60">
                Ultimi 7 giorni
            </p>
        </div>
    );
}
